/*
  RecordingIndicator — a floating pill visible on every page while a
  SOAP recording is in progress. Persists across route changes because
  it renders from the top-level RecordingProvider state.
*/
import { useEffect, useState } from "react";
import { useRecording } from "@/lib/recording";
import { Mic, Square, Loader2, CheckCircle2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

function fmt(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function RecordingIndicator({ onOpen }) {
  const {
    session, status, elapsedMs, pendingUploads, uploadedCount,
    stopRecording, abandonSession, resetSession, isActive,
  } = useRecording();
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    if (status !== "recording") return;
    const id = setInterval(() => setPulse((v) => !v), 800);
    return () => clearInterval(id);
  }, [status]);

  if (!isActive && status !== "done" && status !== "stopped" && status !== "finalizing") return null;

  const label = (() => {
    switch (status) {
      case "starting":   return "Starting…";
      case "recording":  return "Recording";
      case "stopping":   return "Stopping…";
      case "stopped":    return "Ready to finalise";
      case "finalizing": return "Transcribing…";
      case "done":       return "Note ready";
      default:           return status;
    }
  })();

  // Queue-backpressure warning — if pending uploads sit above 2 the pill
  // turns amber so the therapist notices before it becomes a problem.
  const queueBackpressure = status === "recording" && pendingUploads >= 3;

  const tone = (() => {
    if (queueBackpressure) return "bg-amber-500 text-white border-amber-600";
    if (status === "recording") return "bg-red-600 text-white border-red-700";
    if (status === "finalizing" || status === "stopping") return "bg-amber-500 text-white border-amber-600";
    if (status === "done") return "bg-emerald-600 text-white border-emerald-700";
    return "bg-zinc-900 text-white border-zinc-800";
  })();

  return (
    <div
      className={cn(
        "fixed z-[100] bottom-6 right-6 flex items-center gap-3 rounded-full border shadow-lg backdrop-blur-md pr-2 pl-3 py-2",
        tone
      )}
      data-testid="soap-recording-indicator"
    >
      {status === "recording" && (
        <span
          className={cn(
            "h-2.5 w-2.5 rounded-full bg-white transition-opacity",
            pulse ? "opacity-100" : "opacity-30",
          )}
        />
      )}
      {status === "finalizing" || status === "stopping" ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : status === "done" ? (
        <CheckCircle2 className="h-4 w-4" />
      ) : (
        <Mic className="h-4 w-4" />
      )}
      <div className="text-xs font-medium leading-tight">
        <div className="font-semibold">{label}</div>
        {session?.client_name || session?.client_email ? (
          <div className="text-2xs opacity-80 truncate max-w-[160px]">
            {session.client_name || session.client_email}
          </div>
        ) : null}
      </div>
      {status === "recording" && (
        <div className="text-xs font-mono-num tabular-nums px-2 border-l border-white/30">
          {fmt(elapsedMs)}
        </div>
      )}
      {pendingUploads > 0 ? (
        <div
          className="text-2xs px-2 py-0.5 rounded-full bg-white/20 font-mono-num flex items-center gap-1"
          title={`${uploadedCount} segments saved · ${pendingUploads} uploading`}
          data-testid="soap-indicator-upload-counter"
        >
          <span className="tabular-nums">{uploadedCount}</span>
          <span className="opacity-60">saved</span>
          <span className="opacity-60">·</span>
          <span className="tabular-nums">{pendingUploads}</span>
          <span className="opacity-60">queued</span>
        </div>
      ) : (
        uploadedCount > 0 && (status === "recording" || status === "stopped") && (
          <div
            className="text-2xs px-2 py-0.5 rounded-full bg-white/20 font-mono-num flex items-center gap-1"
            title={`${uploadedCount} segments saved to server`}
            data-testid="soap-indicator-upload-counter"
          >
            <CheckCircle2 className="h-3 w-3" />
            <span className="tabular-nums">{uploadedCount}</span>
            <span className="opacity-60">saved</span>
          </div>
        )
      )}
      {status === "recording" && (
        <Button
          size="sm"
          variant="secondary"
          className="h-7 px-2 bg-white text-red-700 hover:bg-white/90"
          onClick={() => {
            stopRecording();
            toast("Recording stopped", {
              description: session?.initiating_assessment_id
                ? "Processing automatically — results will appear on the assessment."
                : "Open Onboarding → Client history to finalise.",
              position: "top-center",
            });
          }}
          data-testid="soap-stop-recording"
        >
          <Square className="h-3.5 w-3.5 mr-1" /> Stop
        </Button>
      )}
      {(status === "stopped" || status === "finalizing" || status === "done") && onOpen && (
        <Button
          size="sm"
          variant="secondary"
          className="h-7 px-2 bg-white text-zinc-900 hover:bg-white/90"
          onClick={() => onOpen(session)}
          data-testid="soap-open-recording"
        >
          Open
        </Button>
      )}
      {status !== "finalizing" && status !== "stopping" && (
        <button
          onClick={async () => {
            if (status === "done") {
              // The generated note is finished — user is dismissing the
              // "Note ready" pill after opening / finalising the note.
              // Clear session state so the pill goes away permanently
              // for this consultation (was previously stuck because
              // this branch did nothing).
              resetSession();
              return;
            }
            if (status === "recording" || status === "starting" || status === "stopped") {
              if (!confirm("Discard this recording? Audio will be deleted.")) return;
              await abandonSession();
            }
          }}
          className="h-6 w-6 grid place-items-center rounded-full hover:bg-white/15"
          title="Dismiss"
          data-testid="soap-discard-recording"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
